import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionLimits {
  maxClients: number | null;
  maxLoans: number | null;
  isPro: boolean;
  hasActiveSubscription: boolean;
  isLoading: boolean;
}

export function useSubscriptionLimits(userId?: string | null): SubscriptionLimits {
  // Backwards compatibility: when called with no argument at all, keep the
  // legacy behaviour (fetch the user inside the query). When called with an
  // explicit userId (even null), gate the query on it to avoid the race
  // condition that returns "no subscription" before the session is ready.
  const explicitUserIdMode = arguments.length > 0;

  const { data, isLoading } = useQuery({
    queryKey: ["subscription-limits", userId ?? null],
    enabled: explicitUserIdMode ? !!userId : true,
    queryFn: async () => {
      let resolvedUserId = userId ?? null;
      if (!explicitUserIdMode) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;
        resolvedUserId = user.id;
      }
      if (!resolvedUserId) return null;

      // Get user's active subscription with plan details
      const { data: subscription, error } = await supabase
        .from("user_subscriptions")
        .select(`
          *,
          plan:subscription_plans(*)
        `)
        .eq("user_id", resolvedUserId)
        .eq("is_active", true)
        .gt("expires_at", new Date().toISOString())
        .maybeSingle();

      if (error) {
        console.error("Error fetching subscription:", error);
        return {
          maxClients: 10,
          maxLoans: 10,
          isPro: false,
          hasActiveSubscription: false,
        };
      }

      if (!subscription) {
        // No active subscription - return free limits
        return {
          maxClients: 10,
          maxLoans: 10,
          isPro: false,
          hasActiveSubscription: false,
        };
      }

      const plan = subscription.plan as any;
      const isPro = plan?.name === "PRO" || plan?.max_clients === null;

      return {
        maxClients: plan?.max_clients ?? 10,
        maxLoans: plan?.max_loans ?? 10,
        isPro,
        hasActiveSubscription: true,
      };
    },
  });

  return {
    maxClients: data?.maxClients ?? 10,
    maxLoans: data?.maxLoans ?? 10,
    isPro: data?.isPro ?? false,
    hasActiveSubscription: data?.hasActiveSubscription ?? false,
    isLoading,
  };
}
